// Painel de uma loja na curadoria automatica: chave liga/desliga, checklist,
// categorias, ritmo, filtros, destinos e atividade. O rascunho vive aqui e so
// vai ao banco no Salvar (ou ao mexer na chave, que salva na hora).

import { useMemo, useState } from "react";
import { Zap } from "lucide-react";
import {
  buildReadiness,
  computePace,
  destinationCount,
  isReady,
  type AutoPromotionConfig,
} from "@lucralink/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { InlineError, Toggle } from "@/components/forwarding/controls";
import { autoPromotionErrorMessage, credentialStateOf } from "@/lib/auto-promotions";
import { useEntitlements } from "@/lib/entitlements";
import {
  useAutoPromotionRuns,
  useAutoPromotionSent,
  useAutoPromotionUsage,
  useSaveAutoPromotionConfig,
  type AutoPromotionState,
} from "@/lib/hooks/useAutoPromotions";
import { findCredentialByMarketplaceCode, useCredentials } from "@/lib/hooks/useCredentials";
import { useForwardingChannels, useForwardingSettings } from "@/lib/hooks/useForwarding";
import { useTenant } from "@/lib/hooks/useTenant";
import { CategoriesCard, DestinationsCard, FiltersCard, PaceCard } from "./ConfigCards";
import { ActivityCard, ReadinessCard } from "./StatusCards";

interface AutoPromotionPanelProps {
  readonly state: AutoPromotionState;
}

function useReadiness(draft: AutoPromotionConfig) {
  const { data: credentials = [] } = useCredentials();
  const { data: settings } = useForwardingSettings();
  const { data: usage } = useAutoPromotionUsage();
  const { data: tenantBilling } = useTenant();
  const entitlements = useEntitlements(tenantBilling?.tenant);
  const dailyQuota = usage?.dailyQuota ?? entitlements.autoPromotionsPerDay;
  const window = settings?.window ?? { enabled: false, outside: "drop" as const, schedule: [] };
  const pace = computePace({
    intervalMinutes: draft.intervalMinutes,
    itemsPerCycle: draft.itemsPerCycle,
    categoryCount: draft.categoryIds.length,
    window,
    dailyQuota,
  });
  const items = buildReadiness({
    credential: credentialStateOf(findCredentialByMarketplaceCode(credentials, "shopee")),
    categoryCount: draft.categoryIds.length,
    destinationCount: destinationCount(draft.destinations),
    pace,
  });
  return { pace, items, dailyQuota, usage, windowEnabled: window.enabled, entitlements };
}

interface EnabledCardProps {
  readonly enabled: boolean;
  readonly ready: boolean;
  readonly dirty: boolean;
  readonly pending: boolean;
  readonly usage: { readonly sentToday: number } | undefined;
  readonly dailyQuota: number | null;
  readonly onToggle: (enabled: boolean) => void;
}

function toggleHint(enabled: boolean, ready: boolean, dirty: boolean): string | undefined {
  if (enabled) return undefined;
  if (dirty) return "Salve as alterações antes de ligar.";
  return ready ? undefined : "Complete o checklist para ligar.";
}

/** Chave liga/desliga: so liga com checklist completo e sem alteracao pendente. */
function EnabledCard({ enabled, ready, dirty, pending, usage, dailyQuota, onToggle }: EnabledCardProps): JSX.Element {
  return (
    <Card className={enabled ? "border-primary/40" : undefined}>
      <CardContent className="space-y-4 p-5">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/15 text-primary">
            <Zap size={18} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <Toggle
              id="auto-promotion-enabled"
              checked={enabled}
              disabled={pending || (!enabled && (!ready || dirty))}
              onChange={onToggle}
              label={enabled ? "Ligado: buscando e enviando promoções" : "Desligado: nada é enviado"}
              hint={toggleHint(enabled, ready, dirty)}
            />
          </div>
        </div>
        {usage && (
          <p className="text-xs text-muted-foreground">
            Hoje: {usage.sentToday}
            {dailyQuota !== null ? ` de ${dailyQuota} promoções do plano Free` : " promoções enviadas"}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function AutoPromotionPanel({ state }: AutoPromotionPanelProps): JSX.Element {
  const [draft, setDraft] = useState<AutoPromotionConfig>(state.config);
  const [feedback, setFeedback] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const save = useSaveAutoPromotionConfig();
  const { data: channels = [] } = useForwardingChannels();
  const { data: runs = [] } = useAutoPromotionRuns(draft.provider);
  const { data: sent = [] } = useAutoPromotionSent(draft.provider);
  const readiness = useReadiness(draft);
  const ready = isReady(readiness.items);
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(state.config), [draft, state.config]);

  const update = (patch: Partial<AutoPromotionConfig>): void => {
    setFeedback(null);
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const persist = async (next: AutoPromotionConfig, okText: string): Promise<void> => {
    try {
      await save.mutateAsync(next);
      setDraft(next);
      setFeedback({ kind: "ok", text: okText });
    } catch (error) {
      setFeedback({ kind: "error", text: autoPromotionErrorMessage(error) });
    }
  };

  const toggleEnabled = (enabled: boolean): void => {
    void persist({ ...draft, enabled }, enabled ? "Promoções automáticas ligadas." : "Promoções automáticas desligadas.");
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="space-y-5">
        <EnabledCard
          enabled={draft.enabled}
          ready={ready}
          dirty={dirty}
          pending={save.isPending}
          usage={readiness.usage}
          dailyQuota={readiness.dailyQuota}
          onToggle={toggleEnabled}
        />
        <CategoriesCard config={draft} onChange={update} />
        <PaceCard
          config={draft}
          pace={readiness.pace}
          dailyQuota={readiness.dailyQuota}
          windowEnabled={readiness.windowEnabled}
          onChange={update}
        />
        <FiltersCard config={draft} onChange={update} />
        <DestinationsCard
          config={draft}
          channels={channels}
          maxDestinations={readiness.entitlements.maxForwardingDestinations}
          onChange={update}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => void persist(draft, "Configuração salva.")} disabled={!dirty || save.isPending}>
            {save.isPending ? "Salvando..." : "Salvar alterações"}
          </Button>
          {feedback?.kind === "ok" && (
            <p role="status" className="text-sm text-primary">
              {feedback.text}
            </p>
          )}
        </div>
        <InlineError message={feedback?.kind === "error" ? feedback.text : null} />
      </div>
      <aside className="space-y-5">
        <ReadinessCard items={readiness.items} />
        <ActivityCard
          runs={runs}
          sent={sent}
          lastError={state.lastError}
          lastErrorAt={state.lastErrorAt}
          nextRunAt={state.nextRunAt}
          enabled={state.config.enabled}
        />
      </aside>
    </div>
  );
}
